import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { components as trelloComponents } from "react-trello";

// ---------------------------------------------------------------------------
// Efeitos de arrastar/soltar compartilhados pelos boards do react-trello
// (Kanban de atendimento e CRM).
//
// O react-trello usa o trello-smooth-dnd, que cria uma cópia do card (o
// "ghost") com `position: fixed` DENTRO da coluna de origem. Por isso:
//
// 1. O zoom do board não pode ser feito com `transform: scale()`: qualquer
//    transform (até scale(1)) vira o bloco de referência do `position: fixed`,
//    e o ghost aparecia deslocado do cursor pela largura do menu lateral +
//    altura do cabeçalho — e encolhido duas vezes quando o zoom era < 100%.
//    Usamos a propriedade CSS `zoom`, que não cria esse bloco de referência.
//
// 2. A lib mede tudo com getBoundingClientRect (pixels de tela já com zoom) e
//    devolve esses valores em `left/top/width` do ghost e no translate dos
//    cards que abrem espaço. Dentro de um elemento com zoom esses pixels
//    seriam multiplicados de novo, então o wrapper de cada card desfaz o zoom
//    (zoom: 1/z) e o card dentro dele reaplica (zoom: z). Como o ghost é um
//    clone do wrapper, ele herda a mesma compensação.
//
// 3. A área que aceita o drop é só o container dos cards. Sem esticá-lo, uma
//    coluna vazia aceitava o card apenas em cima do texto "Nenhum ticket" e
//    soltar abaixo do último card de uma coluna curta não fazia nada.
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  "@keyframes kanbanCardLift": {
    from: {
      transform: "none",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    to: {
      transform: "rotate(2deg) scale(1.03)",
      boxShadow: "0 14px 28px rgba(0,0,0,0.28)",
    },
  },
  "@keyframes kanbanCardSettle": {
    from: {
      transform: "rotate(2deg) scale(1.03)",
      boxShadow: "0 14px 28px rgba(0,0,0,0.28)",
    },
    to: {
      transform: "none",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
  },

  boardZoom: {
    height: "100%",
    // react-trello calcula a altura das colunas a partir do conteúdo (não do
    // container pai): forçamos a coluna a ocupar a altura disponível, para o
    // scroll interno funcionar e a coluna inteira receber o drop.
    "& .smooth-dnd-container.horizontal": {
      height: "100% !important",
    },
    "& .react-trello-lane": {
      height: "100% !important",
      maxHeight: "100% !important",
    },
    "& .smooth-dnd-draggable-wrapper": {
      zoom: "calc(1 / var(--kanban-zoom, 1))",
    },
    "& .smooth-dnd-draggable-wrapper > *": {
      zoom: "var(--kanban-zoom, 1)",
    },
    // os cards abrindo espaço deslizam em vez de pular
    "& .smooth-dnd-draggable-wrapper.animated": {
      transitionTimingFunction: "cubic-bezier(0.2, 0, 0, 1)",
    },
    // ao soltar, o card volta ao normal enquanto desliza até o destino
    "& .smooth-dnd-ghost.animated > *": {
      animation: "$kanbanCardSettle 200ms ease-out forwards",
    },
  },

  // aplicada pelo smooth-dnd no card dentro do ghost enquanto arrasta
  cardDragging: {
    cursor: "grabbing !important",
    animation: "$kanbanCardLift 150ms ease-out forwards",
  },

  laneDragOver: {
    borderRadius: 8,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)",
    boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}55`,
  },

  laneScroll: {
    display: "flex",
    flexDirection: "column",
    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
    "& > .smooth-dnd-container.vertical": {
      flex: "1 0 auto",
    },
  },
}));

export const useKanbanDndStyles = useStyles;

// Estilo do container do board para um nível de zoom.
export const boardZoomStyle = (zoom) => ({
  zoom,
  "--kanban-zoom": zoom,
});

// Área rolável da coluna destacando quando um card está sobre ela. O
// react-trello já calcula o `isDraggingOver`, só não o usa visualmente.
const BaseScrollableLane = trelloComponents.ScrollableLane;

export const DropHighlightLane = React.forwardRef(
  ({ isDraggingOver, className, ...props }, ref) => {
    const classes = useStyles();
    const classNames = [
      className,
      classes.laneScroll,
      isDraggingOver ? classes.laneDragOver : null,
    ]
      .filter(Boolean)
      .join(" ");
    return <BaseScrollableLane ref={ref} className={classNames} {...props} />;
  }
);
